import { MessageSquare, Video, Compass, Headphones, CalendarCheck, Users } from "lucide-react";
import ConsultationModal from "@/components/ConsultationModal";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

/**
 * Onboarding and support.
 *
 * This section used to read as a cSquare managed-services promise: every line
 * said "we". That put the homepage in direct conflict with the channel
 * strategy, which is built on implementation partners doing delivery, and the
 * standing rule that the conflict gets fixed in writing before partners are
 * recruited.
 *
 * The resolution is the product, not a hedge. The onboarding model is
 * cSquare's, our implementation partners are trained to deliver it, and every
 * account has a support partner of record. In the platform, support routes to
 * that partner and defaults to cSquare when none is set. So the copy names both
 * deliverers and the default, which is both accurate and the stronger pitch: a
 * partner reading this page sees the model they were trained on, not a
 * competitor advertising the work they were promised.
 *
 * Keep "we" out of the touchpoint descriptions. The actor is "your guide" or
 * "your partner of record", because on most accounts it will not be us.
 */
const WeAreYourGuide = () => {
  const { t } = useTranslation();

  const touchpoints = [
    { icon: Compass, title: t('weAreYourGuide.guidedSetup'), description: t('weAreYourGuide.guidedSetupDesc') },
    { icon: MessageSquare, title: t('weAreYourGuide.liveSlack'), description: t('weAreYourGuide.liveSlackDesc') },
    { icon: Video, title: t('weAreYourGuide.zoomSessions'), description: t('weAreYourGuide.zoomSessionsDesc') },
    { icon: CalendarCheck, title: t('weAreYourGuide.controlsAssessment'), description: t('weAreYourGuide.controlsAssessmentDesc') },
    { icon: Users, title: t('weAreYourGuide.crossTeam'), description: t('weAreYourGuide.crossTeamDesc') },
    { icon: Headphones, title: t('weAreYourGuide.ongoingPartnership'), description: t('weAreYourGuide.ongoingPartnershipDesc') },
  ];

  return (
    <section className="py-10 bg-background">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <span className="inline-block text-xs font-medium uppercase tracking-wider text-primary mb-3">
            {t('weAreYourGuide.eyebrow')}
          </span>
          <h2 className="section-title-fluid font-bold mb-3">
            {t('weAreYourGuide.title')}{" "}
            <span className="text-primary">{t('weAreYourGuide.titleHighlight')}</span>
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed">
            {t('weAreYourGuide.subtitle')}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border max-w-5xl mx-auto mb-8">
          {touchpoints.map((item, i) => (
            <div key={i} className="group relative bg-background p-5 hover:bg-muted/40 transition-colors duration-200">
              <item.icon className="h-5 w-5 text-primary mb-3" />
              <h3 className="font-semibold text-sm mb-1.5">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <ConsultationModal>
            <Button size="lg" className="px-8">
              {t('weAreYourGuide.meetYourGuide')}
            </Button>
          </ConsultationModal>
        </div>
      </div>
    </section>
  );
};

export default WeAreYourGuide;
